<template>
    <div>
        <v-img v-if="isMobile" eager contain class="img-routine" :src="routineImg"/>
        <v-container :class="[{'pa-0': noContainer}]">
            <loading-spinner v-if="loading"/>
            <v-row v-else>

                <v-col cols="12" md="6">
                    <v-row>
                        <v-col cols="12" v-if="!isMobile">
                            <v-img eager contain class="img-routine" :src="routineImg"/>
                        </v-col>
                        <!-- BASIC INFO -->
                        <v-col cols="12">
                            <v-row class="text-center" no-gutters>
                                <v-col cols="4" class="kimsa-elevation py-3">
                                    <div class="first-row">
                                        {{ $tc(`common.minutes`, 2) }}
                                    </div>
                                    <div class="second-row">
                                        {{ routineDuration }}
                                    </div>
                                </v-col>
                                <v-col cols="4" class="kimsa-elevation py-3">
                                    <div class="first-row">
                                        {{ $tc(`common.blocks`, 2) }}
                                    </div>
                                    <div class="second-row">
                                        {{ blocks.length }}
                                    </div>
                                </v-col>
                                <v-col cols="4" class="kimsa-elevation py-3">
                                    <div class="first-row">
                                        {{ $tc('objectives.objectives', 1) }}
                                    </div>
                                    <div class="second-row">
                                        {{ objective }}
                                    </div>
                                </v-col>
                            </v-row>
                        </v-col>

                        <!-- EQUIPMENTS -->
                        <v-col cols="12" v-if="equipments.length > 0">
                            <div class="equipments text-center">
                                {{ $t('equipments.equip') }}
                            </div>
                            <div class="text-center">
                                <v-chip v-for="(equip,i) in equipments" :key="i" label outlined class="mr-1 mt-1">
                                    <v-img :src="equipImg(equip)" eager contain max-width="35" class="mr-3"/>
                                    {{ equip }}
                                </v-chip>
                            </div>
                        </v-col>

                        <!-- SHOW SUMMARY -->
                        <v-col cols="12" v-if="percentReady === 100">
                            <v-btn block @click="$emit('showSummary')" color="primary" class="normal-btn black--text"
                                   depressed>
                                {{ $t('routines.watch_summary') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>

                <v-col cols="12" md="6">
                    <v-expansion-panels flat accordion v-model="carousel" class="kimsa-elevation">
                        <v-expansion-panel v-for="(block,i) in blocks" :key="i" >
                            <block-expansion-header
                                :hide-student-info="hideStudentInfo"
                                :block="block"
                                :loading-complete="loadingComplete"
                                :readonly="readonly"
                                :index="i"
                                @complete="setBlockComplete"
                            />
                            <v-expansion-panel-content>
                                <div v-for="(exercise,j) in block.exercises" :key="j" class="kimsa-elevation mt-3">
                                    <exercise-card
                                        :exercise="exercise"
                                        :show-complete-sets="showCompleteSets"
                                        @click:goToExercise="goToExercise"
                                        :block="i" :readonly="readonly"
                                        @loadingComplete="isLoadingCompleteExercise = $event"
                                    />
                                    <rest-card :rest="exercise" v-if="exercise.hasTime" readonly/>
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-sheet color="transparent" height="70"/>
                </v-col>

            </v-row>

            <routine-footer v-if="!hideStudentInfo" :percent="percentReady"/>
        </v-container>
    </div>
</template>

<script>

import ExerciseCard from "@/components/common/routines/ExerciseCard";
import RoutineFooter from "@/components/common/routines/RoutineFooter";
import {mapActions, mapMutations, mapState} from "vuex";
import {db} from "@/utils/Firebase";

import {getMergedBlocks} from "@/utils/Exercises";
import RestCard from "@/components/coach/routines/RestCard";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import BlockExpansionHeader from "@/components/common/routines/BlockExpansionHeader";

export default {
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false
        },
        showCompleteSets: {
            type: Boolean,
            default: false
        },
        hideStudentInfo: {
            type: Boolean,
            default: false,
        },
        noContainer: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        BlockExpansionHeader,
        LoadingSpinner,
        RestCard,
        ExerciseCard,
        RoutineFooter,
    },
    data() {
        return {
            loading: false,
            assignedRoutine: null,
            routine: null,
            carousel: 0,
            loadingComplete: false,
        }
    },
    computed: {
        ...mapState(['currentRoutine', 'equipment', 'currentCarousel']),
        routineImg(vm = this) {
            return vm.currentRoutine?.img || vm.imgPlaceholder
        },
        objective(vm = this) {
            return vm.currentRoutine?.description?.objective || vm.$t('common.not_available_short')
        },
        equipments(vm = this) {
            return vm.currentRoutine?.description?.equipments || []
        },
        blocks(vm = this) {
            return vm.currentRoutine?.blocks || []
        },
        routineDuration(vm = this) {
            if (!vm.currentRoutine) return vm.$t('common.not_available_short')

            let minutes = 0

            vm.currentRoutine.blocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise.config && exercise.config.sets.length || 0
                })
            })

            let minutesAdded = Number(minutes * 1.3).toFixed()


            if (Number(minutes) === Number(minutesAdded)) return minutes
            return `${minutes} - ${minutesAdded}`
        },
        percentReady(vm = this) {
            if (!vm.currentRoutine) return 0
            let qtyExercises = 0
            let completed = 0

            vm.currentRoutine.blocks.forEach(block => {
                let exercises = block.exercises.filter(ex => ex.type !== 'rest').length
                if (block.completed) {
                    qtyExercises += exercises
                    completed += exercises
                } else qtyExercises += exercises
            })

            return parseInt((completed * 100) / qtyExercises) || 0
        },
    },
    async mounted() {
        const vm = this
        vm.loading = true
        if (vm.currentCarousel) vm.carousel = vm.currentCarousel
        if (vm.equipment.length === 0) await vm.bindEquipment()
        await vm.setAssignedRoutine()
        vm.loading = false
    },
    methods: {
        ...mapMutations(['setCurrentRoutine', 'setTitle', 'setCurrentCarousel']),
        ...mapActions(['bindEquipment']),
        async setAssignedRoutine() {
            const vm = this
            let assignedRoutineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            vm.assignedRoutine = assignedRoutineRef.data()

            if (vm.assignedRoutine.routineId) {
                let routineRef = await db.collection('routines').doc(vm.assignedRoutine.routineId).get()
                vm.routine = routineRef.data()
            }

            let routineName = vm.assignedRoutine?.name || vm.routine?.name || vm.$t('common.not_available_short')
            vm.setTitle(routineName)

            let currentRoutine = {
                assignedRoutineId: vm.assignedRoutineId,
                name: routineName,
                img: vm.assignedRoutine?.img || vm.routine?.img || null,
                description: vm.routine?.description || null,
                ...vm.assignedRoutine
            }

            currentRoutine.blocks = await getMergedBlocks(vm.assignedRoutine.blocks)
            vm.setCurrentRoutine(currentRoutine)
        },
        equipImg(equip) {
            let vm = this
            let findEquip = vm.equipment.find(el => el.name === equip)
            return findEquip && findEquip.src || vm.imgPlaceholder
        },
        showExerciseDialog(exercise) {
            let vm = this
            vm.$refs.exerciseDialog.show()
            vm.$refs.exerciseDialog.setExercise(exercise)
        },
        goToExercise(exerciseId) {
            const vm = this
            vm.setCurrentCarousel(vm.carousel)
            vm.$router.push({
                name: 'student.routine.detail.exercise',
                params: {
                    id: exerciseId
                }
            })
        },
        async setBlockComplete(completed, block) {
            const vm = this
            vm.loadingComplete = true

            let routineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            let assignedRoutine = routineRef.data()

            assignedRoutine.blocks[block].completed = completed
            assignedRoutine.blocks[block].exercises.map(exercise => {
                if (exercise.type === 'exercise') {
                    exercise.completed = completed
                    let date = vm.$moment().format()
                    let type = completed ? 'complete' : 'incomplete'

                    !exercise.history ?
                        exercise.history = [{date: date, type: type}] :
                        exercise.history.push({date: date, type: type})
                }
            })

            await db.collection('assignedRoutines').doc(vm.assignedRoutineId).update({
                blocks: assignedRoutine.blocks
            })

            await vm.setAssignedRoutine()

            vm.loadingComplete = false
        },
    },
    watch: {
        percentReady(val) {
            if (val === 100)
                setTimeout(() => {
                    this.$emit('complete')
                }, 500)
        }
    }
}
</script>

<style scoped>

.img-routine {
    background-color: rgba(0, 0, 0, .1);
    height: 200px;
}

.first-row {
    font-size: 13px;
    line-height: 20px;
    font-weight: bold;
    text-transform: uppercase;
    color: #666666;
}

.second-row {
    font-size: 17px;
    line-height: 22px;
    font-weight: bold;
    color: #666666;
}

.equipments {
    font-size: 14px;
}

</style>